import { Close, DriveFolderUpload, Edit, Error, Save, SaveAlt } from "@mui/icons-material";
import { Box, Button, FormLabel, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { SyntheticEvent, useCallback } from "react";
import Modal from "../../App/Components/Modal/Modal";
import { TemplateFieldType } from "../../App/Enums/TemplateFieldType";
import { IWelcomeMessageModalState } from "../../App/Typings/Campaigns";
import { Plugins } from "../../MessageComponents/EmailComponents/Plugins";
import TinyEditor from "../../MessageComponents/EmailComponents/TinyEditor";
import { ToolbarWithTemplateFields } from "../../MessageComponents/EmailComponents/Toolbar";
import { TemplateCreateButtonDisableMessage } from "../../MessageComponents/FunctionMethods/MessageFunctions";
import { MemoEmailTemplateListDialog } from "../../MessageComponents/MessageTemplates/EmailTemplateListDialog";
import SaveTemplateOptions from "../../MessageComponents/MessageTemplates/SaveTemplateOptions";
import { MemoTextTemplateListDialog } from "../../MessageComponents/MessageTemplates/TextTemplateListDialog";
import MessageTemplateFields from "../../MessageComponents/SendGroupMessage/Components/MessageTemplateFields/MessageTemplateFields";
import {
  maxWelcomeEmailLength,
  maxWelcomeSubjectLength,
  maxWelcomeTextLength,
} from "../Schema/CampaignFormSchema";
import { useWelcomeMessageModalState } from "../State/WelcomeMessageModalState";

export default function WelcomeMessageModal() {
  const messageModalState = useWelcomeMessageModalState();
  const {
    getters: { formFields, isEmail, messageState, disableSave },
    handlers: {
      handleOpenDialog,
      handleClose,
      handleOpenTemplateDialog,
      handleCloseTemplateDialog,
      handleTemplateSelected,
      handleShowSaveTemplate,
      handleHideSaveTemplate,
      handleTemplateVisibilityChange,
      handleSaveEmailTemplate,
      handleSaveTextTemplate,
      handleTemplateNameChange,
      handleSaveChanges,
      generateButtonTooltipMessage,
      handleAddWelcomeMessageTemplateField,
    },
  } = messageModalState;

  const disableSaveTemplateButton =
    disableSave || !messageState.templateName.length || !messageState.templateVisibility.length;

  const disableTextTemplateMessage = TemplateCreateButtonDisableMessage(
    messageState.templateName,
    messageState.templateVisibility,
    messageState.welcomeMessage,
    false,
    false,
  );
  const disableEmailTemplateMessage = TemplateCreateButtonDisableMessage(
    messageState.templateName,
    messageState.templateVisibility,
    messageState.welcomeMessage,
    false,
    true,
    messageState.welcomeMessageSubject,
  );

  const getExcludedTemplateTypes = useCallback(
    (isEmailBody = false) => {
      const exclusionList = [TemplateFieldType.Recruiter];

      if (!isEmailBody) {
        exclusionList.push(TemplateFieldType.Email);
      }

      const areaOfInterestField = formFields["areaOfInterest"];
      const validAreaOfInterestField =
        !areaOfInterestField ||
        (areaOfInterestField.enabled &&
          areaOfInterestField.required &&
          areaOfInterestField.dropdownOptions.length > 0);

      if (!validAreaOfInterestField) {
        exclusionList.push(TemplateFieldType.Forms);
      }

      return exclusionList;
    },
    [
      formFields["areaOfInterest"]?.enabled,
      formFields["areaOfInterest"]?.required,
      formFields["areaOfInterest"]?.dropdownOptions.length,
    ],
  );

  return (
    <>
      <Tooltip title="Edit Welcome Message">
        <IconButton
          data-test="edit-welcome-message-button"
          aria-label="Edit Welcome Message"
          sx={{ position: "absolute", top: 30, right: 3 }}
          onClick={handleOpenDialog}
        >
          <Edit color="primary" />
        </IconButton>
      </Tooltip>
      <Modal
        title={`Edit Welcome ${isEmail ? "Email" : "Text Message"}`}
        isOpen={messageState.open}
        handleCloseModal={handleClose}
        dataTestId="edit-welcome-message"
        size="md"
        disableEnforceFocus={true}
        content={
          <>
            <Typography variant="caption">
              Enter [[FirstName]] or [[LastName]] to personalize the message for each candidate.
            </Typography>
            <Typography variant="caption" component="p" sx={{ mt: 1 }}>
              Use [[ManageSubscription]] to include an unsubscribe link in your email to comply with anti-spam
              laws. Only available in Email Body fields. <br />
              <em>
                Note: If excluded, the required unsubscribe link will be automatically included with your sent
                emails.
              </em>
            </Typography>
            <Box sx={{ px: 1, pt: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  mb: 1,
                }}
              >
                {isEmail ? (
                  <>
                    <EmailSubjectInput
                      state={messageModalState}
                      TemplateFields={
                        <MessageTemplateFields
                          excludedTypes={getExcludedTemplateTypes(false)}
                          handleTemplateButtonClick={(_e, field) =>
                            handleAddWelcomeMessageTemplateField(field, isEmail, true)
                          }
                        />
                      }
                    />
                    <EmailBodyInput
                      state={messageModalState}
                      TemplateFields={
                        <MessageTemplateFields
                          excludedTypes={getExcludedTemplateTypes(true)}
                          handleTemplateButtonClick={(_e, field) =>
                            handleAddWelcomeMessageTemplateField(field, isEmail, false)
                          }
                        />
                      }
                    />
                  </>
                ) : (
                  <TextBodyInput
                    state={messageModalState}
                    TemplateFields={
                      <MessageTemplateFields
                        excludedTypes={getExcludedTemplateTypes(false)}
                        handleTemplateButtonClick={(_e, field) =>
                          handleAddWelcomeMessageTemplateField(field, isEmail, false)
                        }
                      />
                    }
                  />
                )}
              </Box>

              <SaveTemplateOptions
                showSaveTemplate={messageState.saveTemplateActive}
                handleSaveTemplateNow={isEmail ? handleSaveEmailTemplate : handleSaveTextTemplate}
                handleTemplateNameChange={handleTemplateNameChange}
                handleTemplateVisbilityChange={handleTemplateVisibilityChange}
                templateName={messageState.templateName}
                templateVisibility={messageState.templateVisibility}
                disableButton={disableSaveTemplateButton}
                disableMessage={isEmail ? disableEmailTemplateMessage : disableTextTemplateMessage}
              />
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexDirection: { xs: "column", md: "row" },
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {!messageState.saveTemplateActive && (
                  <Tooltip title={generateButtonTooltipMessage()} arrow>
                    <Box>
                      <Button
                        data-test="save-changes-button"
                        aria-label="Save Changes"
                        variant="contained"
                        startIcon={<Save />}
                        disabled={disableSave || messageState.saveTemplateActive}
                        onClick={handleSaveChanges}
                        id="add-edit-field-save"
                      >
                        {isEmail ? "Save Welcome Email" : "Save Welcome Text Message"}
                      </Button>
                    </Box>
                  </Tooltip>
                )}
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    ml: { xs: 0, md: "auto" },
                    flexDirection: { xs: "column", md: "row" },
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    {messageState.saveTemplateActive ? (
                      <Button
                        data-test="save-template-cancel"
                        aria-label="Cancel Save Template"
                        variant="outlined"
                        color="error"
                        startIcon={<Close />}
                        onClick={handleHideSaveTemplate}
                        id="save-template-cancel"
                      >
                        Cancel Save Template
                      </Button>
                    ) : (
                      <Button
                        data-test="save-template"
                        aria-label="Save As Template"
                        variant="outlined"
                        startIcon={<SaveAlt />}
                        disabled={disableSave}
                        onClick={handleShowSaveTemplate}
                        id="save-template"
                      >
                        Save as Template
                      </Button>
                    )}
                  </Box>
                  {isEmail ? (
                    <MemoEmailTemplateListDialog
                      ButtonProps={{ variant: "outlined" }}
                      buttonName={"Email Templates"}
                      handleSelected={(template) => {
                        handleTemplateSelected(template);
                      }}
                      showDialog={messageState.templateOpen}
                      handleCloseEmailTemplateDialog={handleCloseTemplateDialog}
                      handleShowEmailTemplateClick={handleOpenTemplateDialog}
                    />
                  ) : (
                    <MemoTextTemplateListDialog
                      ButtonProps={{ variant: "outlined", startIcon: <DriveFolderUpload /> }}
                      buttonName={"Load Text Templates"}
                      handleSelected={(template) => {
                        handleTemplateSelected(template);
                      }}
                      showDialog={messageState.templateOpen}
                      handleCloseTextTemplateDialog={handleCloseTemplateDialog}
                      handleShowTextTemplateClick={handleOpenTemplateDialog}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </>
        }
        footerContent={<></>}
      />
    </>
  );
}

const TextBodyInput = ({
  state,
  TemplateFields,
}: {
  state: IWelcomeMessageModalState;
  TemplateFields?: JSX.Element;
}) => {
  const {
    getters: {
      messageState: { welcomeMessage, messageLengthErrorMessage, textSplitWarning, textSplitCount },
    },
    handlers: { handleEditWelcomeMessage, handleFocusInput, handleSubmitFromEnter },
  } = state;
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="subtitle1" sx={{ mb: 1 }} component={FormLabel} htmlFor="new-welcome-subject">
          Text Body
        </Typography>
        {TemplateFields ? TemplateFields : null}
      </Stack>
      <TextField
        multiline
        data-test="welcome-message-text-field"
        aria-label="Edit Welcome Text Message"
        placeholder="Welcome to our talent network!"
        name="new-welcome-message"
        id="new-welcome-message"
        value={welcomeMessage}
        onChange={handleEditWelcomeMessage}
        onKeyDown={handleSubmitFromEnter}
        onFocus={handleFocusInput}
        sx={{ mb: 1 }}
      />
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {!!messageLengthErrorMessage && (
          <Typography variant="caption" sx={{ fontStyle: "italic", color: "error.main" }}>
            {messageLengthErrorMessage}
          </Typography>
        )}
        <Typography
          variant="caption"
          sx={{
            fontStyle: "italic",
            color: messageLengthErrorMessage ? "error.main" : "primary",
            ml: { xs: 0, md: "auto" },
          }}
        >
          {welcomeMessage?.length}/{maxWelcomeTextLength}
        </Typography>
        {textSplitWarning && (
          <Tooltip title={textSplitWarning}>
            <Typography
              variant="caption"
              sx={{
                display: "flex",
                gap: "0.25em",
                alignItems: "center",
                fontStyle: "italic",
                color: "warning.main",
              }}
            >
              {textSplitCount} SMS <Error color="error" />
            </Typography>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const EmailSubjectInput = ({
  state,
  TemplateFields,
}: {
  state: IWelcomeMessageModalState;
  TemplateFields?: JSX.Element;
}) => {
  const {
    getters: {
      messageState: { welcomeMessageSubject, subjectLengthErrorMessage },
    },
    handlers: { handleEditWelcomeSubject, handleFocusInput, handleSubmitFromEnter },
  } = state;
  return (
    <Box sx={{ mb: 3, display: "flex", flexDirection: "column" }}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="subtitle1" sx={{ mb: 1 }} component={FormLabel} htmlFor="new-welcome-subject">
          Email Subject
        </Typography>
        {TemplateFields}
      </Stack>
      <TextField
        data-test="welcome-subject-text-field"
        placeholder="Hello, [[FirstName]]!"
        aria-label="Edit Welcome Subject"
        name="new-welcome-subject"
        id="new-welcome-subject"
        value={welcomeMessageSubject}
        onChange={handleEditWelcomeSubject}
        onKeyDown={handleSubmitFromEnter}
        onFocus={handleFocusInput}
        sx={{ mb: 1 }}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {!!subjectLengthErrorMessage && (
          <Typography variant="caption" sx={{ fontStyle: "italic", color: "error.main" }}>
            {subjectLengthErrorMessage}
          </Typography>
        )}
        <Typography
          variant="caption"
          sx={{
            fontStyle: "italic",
            color: subjectLengthErrorMessage ? "error.main" : "primary",
            ml: "auto",
          }}
        >
          {welcomeMessageSubject?.length || 0}/{maxWelcomeSubjectLength}
        </Typography>
      </Box>
    </Box>
  );
};

const EmailBodyInput = ({
  state,
  TemplateFields,
}: {
  state: IWelcomeMessageModalState;
  TemplateFields?: JSX.Element;
}) => {
  const {
    getters: {
      messageState: { welcomeMessage, messageLengthErrorMessage },
    },
    handlers: { handleEditWelcomeMessage },
  } = state;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          "& .tox-tinymce": {
            border: "1px solid rgba(70,70,70,0.3)",
            borderRadius: 1,
            mb: 1,
          },
        }}
        data-test="welcome-message-email-body"
      >
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="subtitle1" sx={{ mb: 1 }} component={FormLabel} htmlFor="new-welcome-message">
            Email Body
          </Typography>
          {TemplateFields ? TemplateFields : null}
        </Stack>
        <TinyEditor
          onInit={state.setters.setEditor}
          value={welcomeMessage}
          plugins={Plugins}
          toolbar={ToolbarWithTemplateFields}
          disabled={false}
          textareaName="new-welcome-message"
          onEditorChange={(contentHtml, editor) => {
            const contentTextOnly = editor.getContent({ format: "text" }).trim();
            const messageEventObject = {
              currentTarget: { value: contentTextOnly.length ? contentHtml : "" },
            } as SyntheticEvent<HTMLInputElement | HTMLTextAreaElement, Event>;
            handleEditWelcomeMessage(messageEventObject);
          }}
        />
      </Box>
      <Box
        sx={{
          // CRM-485 Remove email length validation until Large Email Templates are implemented
          // display: "flex",
          display: "none",
          gap: 1,
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {!!messageLengthErrorMessage && (
          <Typography variant="caption" sx={{ fontStyle: "italic", color: "error.main" }}>
            {messageLengthErrorMessage}
          </Typography>
        )}
        <Typography
          variant="caption"
          sx={{
            fontStyle: "italic",
            color: messageLengthErrorMessage ? "error.main" : "primary",
            ml: { xs: 0, md: "auto" },
          }}
        >
          {welcomeMessage?.length}/{maxWelcomeEmailLength}
        </Typography>
      </Box>
    </Box>
  );
};
